import axios from "axios";

const BASE = process.env.REACT_APP_BACKEND_URL;

function getCookie(name) {
  const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return m ? decodeURIComponent(m[2]) : null;
}

const api = axios.create({ baseURL: `${BASE}/api`, withCredentials: true });

api.interceptors.request.use((cfg) => {
  const method = (cfg.method || "get").toLowerCase();
  if (["post", "patch", "put", "delete"].includes(method)) {
    const csrf = getCookie("rep_csrf");
    if (csrf) cfg.headers["X-CSRF-Token"] = csrf;
  }
  return cfg;
});

export function errMsg(err) {
  const d = err?.response?.data?.detail;
  if (!d) return err?.message || "Something went wrong.";
  if (typeof d === "string") return d;
  // FastAPI / Pydantic validation errors arrive as an array of {loc, msg, type}.
  if (Array.isArray(d)) {
    const first = d[0];
    if (first?.msg) return first.msg.replace(/^Value error,\s*/i, "");
    return "Please check the details you entered.";
  }
  return d.message || "Something went wrong.";
}

export const API_BASE = `${BASE}/api`;

export const saas = {
  login: (email, password) => api.post("/auth/login", { email, password }),
  register: (payload) => api.post("/auth/register", payload),
  logout: () => api.post("/auth/logout"),
  me: () => api.get("/auth/me"),
  changePassword: (currentPassword, newPassword) => api.post("/auth/change-password", { currentPassword, newPassword }),

  summary: () => api.get("/app/summary"),
  billing: () => api.get("/app/billing"),
  selectPlan: (plan) => api.post("/app/billing/select-plan", { plan }),
  createCheckoutSession: (plan) => api.post("/billing/create-checkout-session", { plan, originUrl: window.location.origin }),
  customerPortal: () => api.post("/billing/customer-portal"),
  switchPlan: (plan) => api.post("/billing/switch-plan", { plan }),
  listProjects: () => api.get("/app/projects"),
  createProject: (projectName, ratio) => api.post("/app/projects", { projectName, ratio }),
  getProject: (id) => api.get(`/app/projects/${id}`),
  reserve: (id) => api.post(`/app/projects/${id}/reserve`),
  release: (id) => api.post(`/app/projects/${id}/release`),
  uploadPhoto: (id, form, onUploadProgress) => api.post(`/app/projects/${id}/photos`, form, { onUploadProgress, timeout: 180000 }),
  generate: (id) => api.post(`/app/projects/${id}/generate`),
  status: (id) => api.get(`/app/projects/${id}/status`),
  loadVideos: (id) => api.post(`/app/projects/${id}/videos`),
  export: (id) => api.post(`/app/projects/${id}/export`),
  exportStatus: (id, jobId) => api.get(`/app/projects/${id}/export/${jobId}`),
  clipsZip: (id) => api.get(`/app/projects/${id}/clips-zip`, { responseType: "blob", timeout: 180000 }),
  videoClipsZip: (id, index) => api.get(`/app/projects/${id}/videos/${index}/clips-zip`, { responseType: "blob", timeout: 180000 }),

  contact: (payload) => api.post("/contact", payload),

  adminOverview: () => api.get("/admin/overview"),
  adminPlans: () => api.get("/admin/plans"),
  adminOrgs: () => api.get("/admin/organisations"),
  adminCreateOrg: (payload) => api.post("/admin/organisations", payload),
  adminOrgStatus: (id, status) => api.post(`/admin/organisations/${id}/status`, { status }),
  adminOrgPlan: (id, planId) => api.patch(`/admin/organisations/${id}/plan`, { planId }),
  adminLedger: (organisationId) => api.get("/admin/credits/ledger", { params: organisationId ? { organisationId } : {} }),
  adminGrant: (payload) => api.post("/admin/credits/grant", payload),
  adminProjects: () => api.get("/admin/projects"),
  adminDiagnostics: () => api.get("/admin/diagnostics"),
  adminStripeEvents: () => api.get("/admin/stripe/events"),
  adminGetPool: () => api.get("/admin/settings/provider-pool"),
  adminSetPool: (balance, note) => api.post("/admin/settings/provider-pool", { balance, note }),
  adminTopupPool: (amount, reason) => api.post("/admin/settings/provider-pool/topup", { amount, reason }),
  adminRefund: (projectId, reason) => api.post(`/admin/projects/${projectId}/refund`, { reason }),
  adminMessages: () => api.get("/admin/messages"),
  adminMessageRead: (id) => api.post(`/admin/messages/${id}/read`),
  adminOrgUsers: (orgId) => api.get(`/admin/organisations/${orgId}/users`),
  adminResetUserPassword: (userId) => api.post(`/admin/users/${userId}/reset-password`),
  adminOrgName: (orgId, name) => api.patch(`/admin/organisations/${orgId}/name`, { name }),

  // Demo accounts (OWNER_ADMIN only)
  adminCreateDemo: (payload) => api.post("/admin/demo-accounts", payload),
  adminExtendDemo: (orgId, expiryDays) => api.post(`/admin/demo-accounts/${orgId}/extend`, { expiryDays }),
  adminRevokeDemo: (orgId) => api.post(`/admin/demo-accounts/${orgId}/revoke-credit`),
  adminOrgNote: (orgId, note) => api.patch(`/admin/organisations/${orgId}/note`, { note }),
  adminCapacity: () => api.get("/admin/capacity"),
};
